import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BG_IMAGE = "https://images.unsplash.com/photo-1579003223313-8053dc9d10aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxNYWtrYWglMjBLYWFiYSUyMG5pZ2h0JTIwdmlldyUyMGhpZ2glMjBxdWFsaXR5fGVufDB8fHx8MTc3MDU0MDA3OHww&ixlib=rb-4.1.0&q=85";
const AKIT_LOGO = "https://customer-assets.emergentagent.com/job_dev-auth-platform/artifacts/3yr5z3x8_AKIT%20LOGO%202%281%29.png";

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await forgotPassword(email);
      setSubmitted(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ 
        backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.7)), url(${BG_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="w-full max-w-md">
        <div className="glass-card-heavy rounded-2xl p-8">
          {submitted ? (
            <div className="text-center">
              <div className="h-16 w-16 rounded-full bg-primary/20 mx-auto flex items-center justify-center mb-6">
                <CheckCircle className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground mb-4">Check Your Email</h1>
              <p className="text-muted-foreground mb-8">
                We've sent password reset instructions to <span className="text-primary">{email}</span>. 
                Please check your inbox and follow the link to reset your password.
              </p>
              <Link to="/login">
                <Button className="btn-primary" data-testid="back-to-login">
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="text-center mb-8">
                <Link to="/" className="inline-block mb-4">
                  <img 
                    src={AKIT_LOGO} 
                    alt="AKIT Logo" 
                    className="h-20 w-20 mx-auto rounded-xl object-contain"
                  />
                </Link>
                <h1 className="text-2xl font-heading font-bold text-foreground mb-2">Forgot Password?</h1>
                <p className="text-muted-foreground text-sm">
                  Enter your email address and we'll send you instructions to reset your password.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground ml-1">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="name@akit.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-glass pl-12 h-14"
                      required
                      data-testid="forgot-email"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary h-14 text-lg" 
                  disabled={loading}
                  data-testid="forgot-submit"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      Send Reset Link
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>

              {/* Footer */}
              <div className="mt-8 text-center">
                <Link 
                  to="/login" 
                  className="text-muted-foreground text-sm hover:text-primary transition-colors inline-flex items-center"
                  data-testid="back-link"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
